import { useCallback } from 'react';
import { useFlagService } from '@atlassian/jira-flags';

import {
	ADMIN_CONSENT_REQUIRED_EVENT_ID,
	JIRA_SUPPORT_LINK,
	MSTEAMS_CREATE_CONNECTION_EVENT_ID,
	MSTEAMS_USER_LEVEL_AUTH_EVENT_ID,
	MstErrorCodes,
} from '../../common/constants';
import { chatOpsMessageHandler } from '../../common/utils/connection';
import messages from './messages';

export const useMSTConnection = ({
	onSuccess,
}: {
	onSuccess: () => Promise<PromiseSettledResult<void>[]>;
}) => {
	const { showFlag } = useFlagService();

	const mstConnectHandler = useCallback(
		(event: MessageEvent) => {
			chatOpsMessageHandler({
				event,
				requiredEventId: MSTEAMS_CREATE_CONNECTION_EVENT_ID,
				onSuccessCallback: onSuccess,
				onFailedCallback: (errorCode: unknown) => {
					switch (errorCode) {
						case MstErrorCodes.TENANT_CONFLICT: {
							showFlag({
								type: 'error',
								title: messages.tenantConflictErrorTitleNonFinal,
								description: messages.tenantConflictErrorDescriptionNonFinal,
							});
							break;
						}
						case MstErrorCodes.TENANT_MISMATCH: {
							showFlag({
								type: 'error',
								title: messages.tenantMissmatchErrorTitleNonFinal,
								description: messages.tenantMissmatchErrorDescriptionNonFinal,
							});
							break;
						}
						default: {
							showFlag({
								type: 'error',
								title: messages.teamsLoginErrorTitleNonFinal,
								description: messages.teamsLoginErrorTitleNonFinal,
								actions: [
									{
										content: messages.contactSupportBtnLabelNonFinal,
										href: JIRA_SUPPORT_LINK,
										target: '_blank',
									},
								],
							});
						}
					}
				},
			});
		},
		[onSuccess, showFlag],
	);

	const adminAuthoriseHandler = useCallback(
		(event: MessageEvent) => {
			chatOpsMessageHandler({
				event,
				requiredEventId: ADMIN_CONSENT_REQUIRED_EVENT_ID,
				onSuccessCallback: () => {
					onSuccess();
				},
				onFailedCallback: () => {
					showFlag({
						type: 'error',
						title: messages.teamsLoginErrorTitleNonFinal,
						description: messages.teamsLoginErrorTitleNonFinal,
						actions: [
							{
								content: messages.contactSupportBtnLabelNonFinal,
								href: JIRA_SUPPORT_LINK,
								target: '_blank',
							},
						],
					});
				},
			});
		},
		[onSuccess, showFlag],
	);

	const mstUserLevelAuthoriseHandler = useCallback(
		(event: MessageEvent) => {
			chatOpsMessageHandler({
				event,
				requiredEventId: MSTEAMS_USER_LEVEL_AUTH_EVENT_ID,
				onSuccessCallback: () => {
					onSuccess();
				},
			});
		},
		[onSuccess],
	);

	return {
		mstConnectHandler,
		adminAuthoriseHandler,
		mstUserLevelAuthoriseHandler,
	};
};
